import { gql } from "@apollo/client";
import { Editable, Grid, GridItem, Heading, IconButton } from "@chakra-ui/react";
import { closestCenter, DndContext, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { LuTrash2 } from "react-icons/lu";
import { MdDragIndicator } from "react-icons/md";
import type { CategorySettingsForm_CategoryFragment } from "../graphql/generated/apollo";
import { Button } from "./ui/button";

gql`
  fragment CategorySettingsForm_Category on categories {
    id
    name
    position
  }
`;

type CategorySettingsFormProps = {
  categories: CategorySettingsForm_CategoryFragment[];
  onAdd: (category: Omit<CategorySettingsForm_CategoryFragment, "id">) => void;
  onUpdate: (category: CategorySettingsForm_CategoryFragment) => void;
  onDelete: (id: string) => void;
  onReorder: (categories: CategorySettingsForm_CategoryFragment[]) => void;
};

// ドラッグ可能なアイテムコンポーネント
function SortableItem({
  category,
  onUpdate,
  onDelete,
}: {
  category: CategorySettingsForm_CategoryFragment;
  onUpdate: (category: CategorySettingsForm_CategoryFragment) => void;
  onDelete: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: category.id || `new-${category.position}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: "100%",
  };

  return (
    <Grid
      ref={setNodeRef}
      style={style}
      templateColumns="40px 1fr 60px"
      gap={2}
      alignItems="center"
      p={2}
      borderBottomWidth="1px"
      borderBottomColor="gray.200"
    >
      <GridItem>
        <IconButton aria-label="並び替え" variant="ghost" size="sm" cursor="grab" {...attributes} {...listeners}>
          <MdDragIndicator />
        </IconButton>
      </GridItem>
      <GridItem>
        <Editable.Root
          defaultValue={category.name}
          onValueCommit={(details) => onUpdate({ ...category, name: details.value })}
        >
          <Editable.Preview />
          <Editable.Input textAlign="left" />
        </Editable.Root>
      </GridItem>
      <GridItem justifySelf="flex-end">
        <IconButton
          aria-label="削除"
          colorPalette="red"
          variant="ghost"
          size="sm"
          onClick={() => category.id && onDelete(category.id)}
        >
          <LuTrash2 />
        </IconButton>
      </GridItem>
    </Grid>
  );
}

export function CategorySettingsForm({ categories, onAdd, onUpdate, onDelete, onReorder }: CategorySettingsFormProps) {
  // ドラッグ終了時の処理
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = categories.findIndex((item) => (item.id || `new-${item.position}`) === active.id);
      const newIndex = categories.findIndex((item) => (item.id || `new-${item.position}`) === over.id);

      const newCategories = arrayMove(categories, oldIndex, newIndex).map((item, index) => ({
        ...item,
        position: index,
      }));

      onReorder(newCategories);
    }
  };

  // 新しいカテゴリーの追加
  const handleAddCategory = () => {
    const newPosition = categories.length > 0 ? Math.max(...categories.map((item) => item.position)) + 1 : 0;
    const newCategory = { name: `カテゴリー${newPosition}`, position: newPosition };

    onAdd(newCategory);
  };

  return (
    <>
      <Heading fontSize="lg" textAlign="left" py={2}>
        カテゴリー設定
      </Heading>
      <Heading fontSize="sm" textAlign="left" py={2}>
        カテゴリー管理
      </Heading>
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={categories.map((item) => item.id || `new-${item.position}`)}
          strategy={verticalListSortingStrategy}
        >
          <Grid borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
            {categories.map((category) => (
              <SortableItem
                key={category.id || `new-${category.position}`}
                category={category}
                onUpdate={onUpdate}
                onDelete={onDelete}
              />
            ))}
          </Grid>
        </SortableContext>
      </DndContext>

      <Button type="button" onClick={handleAddCategory} mt={4}>
        カテゴリを追加
      </Button>
    </>
  );
}
